// OrderConfirmation Page - Success page after placing orders
import { useEffect, useState, useRef, Fragment } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle,
  Clock,
  Package,
  Truck,
  ShieldAlert,
  AlertCircle,
  Ban,
  Copy,
  Check,
} from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button } from '../../components/radix'
import { api } from '../../services/api'
import { useCart } from '../../auth/hooks/useCart'
import { getStatusLabel } from '../../constants/orderStatus'
import { formatShortDate, toDateKey } from '../../utils/fulfillmentDates'

interface OrderItem {
  id: string
  productName: string
  quantity: number
  unitPrice: number
  totalPrice: number
  imageUrl?: string
}

interface BranchDetails {
  address: string
  postcode: string
  phone: string
}

interface DeliveryAddress {
  line_1: string
  line_2?: string
  line_3?: string
  post_town: string
  postcode: string
}

interface OrderData {
  id: string
  orderNumber: string
  jobNumber?: string
  supplierId?: number
  supplierName: string
  branchName: string
  total: number
  fulfillmentType: 'pickup' | 'delivery'
  itemCount: number
  items: OrderItem[]
  branchDetails: BranchDetails | null
  deliveryAddress: DeliveryAddress | null
  requiresApproval: boolean
  approvalReasons: string[]
  assignedApprover: string | null
  status: string
  placedAt: string
  submissionWarning?: string
  // DEV-51011 AC9: the day the order is booked for; null = the branch confirms it.
  bookedDate: string | null
  // True when the supplier gave no date, so the booked day is only a request.
  isBookedDateUnconfirmed: boolean
  // True when the engineer chose the day in the checkout date sheet.
  hasRequestedDate: boolean
  // Pickup guidance from the backend (DGM-003); null when the message is hidden.
  collectionGuidance: { text: string; variant: string; date: string | null } | null
  // Delivery guidance from the backend (DGM-004); null when the message is hidden.
  deliveryGuidance: { text: string; variant: string; date: string | null } | null
}

// Terminal statuses that don't need polling
const TERMINAL_STATUSES = new Set([
  'completed', 'cancelled', 'internal_decline',
  'fulfillment_declined', 'supplier_cancelled', 'submission_failed',
  'withdrawn',
])

// Statuses that indicate order is still being submitted or awaiting supplier
const SUBMITTING_STATUSES = new Set([
  'started_processing', 'approved', 'awaiting_supplier',
])

export function OrderConfirmationPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { cart } = useCart()
  const [orders, setOrders] = useState<OrderData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasInvalidatedCart, setHasInvalidatedCart] = useState(false)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Copy-to-clipboard state, keyed by order id so each card on this multi-order
  // page tracks its own status independently.
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Copy a shareable summary (APR-006 AC2): the job reference (when set), the
  // order reference, and a link. An approval order links to the APPROVER
  // order-detail so a shared approver lands where they can act; a normal placed
  // order links to the engineer's own page — engineers can't open the
  // /:tenant/* admin route (AdminLayoutGuard redirects them). No supplier
  // transaction id.
  const handleCopyOrder = async (order: OrderData) => {
    const lines: string[] = []
    if (order.jobNumber) {
      lines.push(`Job number: ${order.jobNumber}`)
    }
    lines.push(`Order ID: ${order.orderNumber}`)
    const orderLink = order.requiresApproval
      ? `${window.location.origin}/${tenantSlug}/orders/${order.id}`
      : `${window.location.origin}/${tenantSlug}/engineer/order/${order.id}`
    lines.push(`Order Link: ${orderLink}`)
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      setCopiedId(order.id)
      setTimeout(() => setCopiedId((current) => (current === order.id ? null : current)), 2000)
    } catch {
      // Clipboard unavailable (insecure context / denied permission) — no-op.
    }
  }

  // Get order IDs from URL params
  const orderIdsParam = searchParams.get('orders')
  const orderIds = orderIdsParam ? orderIdsParam.split(',').filter(Boolean) : []

  // Function to fetch orders
  const fetchOrders = async (isPolling = false) => {
    if (orderIds.length === 0) {
      setIsLoading(false)
      return
    }

    if (!isPolling) {
      setIsLoading(true)
      setError(null)
    }

    try {
      // Fetch all orders in parallel
      const orderPromises = orderIds.map((id) =>
        api.getOrder(id).catch((err) => {
          console.error(`Failed to fetch order ${id}:`, err)
          return null
        })
      )

      const results = await Promise.all(orderPromises)

      // Filter out failed fetches and map to OrderData
      const fetchedOrders: OrderData[] = results
        .filter((order): order is NonNullable<typeof order> => order !== null)
        .map((order) => ({
          id: order.id,
          orderNumber: order.order_number || order.id.slice(0, 8),
          jobNumber: order.job_number || undefined,
          supplierId: order.supplier_id != null ? Number(order.supplier_id) : undefined,
          supplierName: order.supplier_name || 'Unknown supplier',
          branchName: order.branch_name || 'Unknown branch',
          total: parseFloat(order.total_amount) || 0,
          fulfillmentType: (order.fulfillment_type === 'collection' ? 'pickup' : order.fulfillment_type || 'delivery') as 'pickup' | 'delivery',
          itemCount: order.items?.length || 0,
          items: (order.items || []).map((item) => ({
            id: item.id,
            productName: item.product_name || item.product_snapshot?.name || 'Unknown product',
            quantity: item.quantity,
            unitPrice: parseFloat(item.unit_price) || 0,
            totalPrice: parseFloat(item.total_price) || 0,
            imageUrl: item.product_snapshot?.image_url,
          })),
          branchDetails: order.branch_details || (order.branch_address ? {
            address: order.branch_address,
            postcode: order.postcode || '',
            phone: order.branch_phone || '',
          } : null),
          deliveryAddress: order.line_1 ? {
            line_1: order.line_1,
            line_2: order.line_2 || undefined,
            line_3: order.line_3 || undefined,
            post_town: order.post_town || '',
            postcode: order.postcode || '',
          } : (order.delivery_address ? {
            line_1: order.delivery_address.line_1,
            line_2: order.delivery_address.line_2 || undefined,
            line_3: order.delivery_address.line_3 || undefined,
            post_town: order.delivery_address.post_town || '',
            postcode: order.delivery_address.postcode || '',
          } : null),
          requiresApproval: order.status === 'awaiting_approval',
          approvalReasons: order.approval_reasons ?? [],
          assignedApprover: order.approver_name || null,
          status: order.status,
          placedAt: order.created_at,
          submissionWarning: order.submission_error || undefined,
          // Resolved by the backend with the submission rule, on the day it
          // ran (F25); suppliers sent no date keep the supplier's own date.
          bookedDate: order.booked_fulfillment_date ?? toDateKey(order.expected_fulfillment_date),
          isBookedDateUnconfirmed: !order.expected_fulfillment_date,
          hasRequestedDate: !!order.requested_fulfillment_date,
          collectionGuidance: order.collection_guidance ?? null,
          deliveryGuidance: order.delivery_guidance ?? null,
        }))

      setOrders(fetchedOrders)

      // Check if any order is now "placed" (supplier accepted) and cart hasn't been invalidated yet
      const anyPlaced = fetchedOrders.some((order) => !SUBMITTING_STATUSES.has(order.status) && !TERMINAL_STATUSES.has(order.status))
      if (anyPlaced && !hasInvalidatedCart) {
        // Invalidate cart cache - backend has cleared cart items
        queryClient.invalidateQueries({ queryKey: ['cart'] })
        setHasInvalidatedCart(true)
        console.log('[OrderConfirmation] Order placed by supplier, invalidating cart cache')
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err)
      if (!isPolling) {
        setError('Failed to load order details')
      }
    } finally {
      if (!isPolling) {
        setIsLoading(false)
      }
    }
  }

  // Initial fetch
  useEffect(() => {
    fetchOrders()
  }, [orderIdsParam]) // Only re-fetch when URL param changes

  // Poll for order status updates while orders are processing
  useEffect(() => {
    const anySubmitting = orders.some(
      (order) => SUBMITTING_STATUSES.has(order.status)
    )

    if (anySubmitting && !hasInvalidatedCart) {
      // Start polling every 3 seconds
      pollingIntervalRef.current = setInterval(() => {
        fetchOrders(true)
      }, 3000)
    } else {
      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [orders, hasInvalidatedCart])

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Redirect if no order IDs in URL
  useEffect(() => {
    if (!isLoading && orderIds.length === 0) {
      navigate(`/${tenantSlug}/engineer/cart`)
    }
  }, [isLoading, orderIds.length, navigate, tenantSlug])

  // Loading state
  if (isLoading) {
    return (
      <EngineerLayout>
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4" />
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </EngineerLayout>
    )
  }

  // Error state
  if (error) {
    return (
      <EngineerLayout>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-red-900 font-bold text-lg mb-2">Error loading orders</h2>
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={() => navigate(`/${tenantSlug}/engineer/orders`)}>
              View all orders
            </Button>
          </div>
        </div>
      </EngineerLayout>
    )
  }

  // No orders found
  if (orders.length === 0) {
    return null
  }

  const anyRequiresApproval = orders.some((order) => order.requiresApproval)
  // AC5: weekly-limit surfaces a reason-specific highlight in the subtitle.
  // Basket value, restricted item, restricted category, restricted supplier,
  // and restricted branch all fall through to the generic approval message.
  const anyWeeklyLimit = orders.some((order) => order.approvalReasons?.includes('weekly_limit'))
  const anySubmitting = orders.some(
    (order) => SUBMITTING_STATUSES.has(order.status)
  )
  const allPlaced = orders.every((order) => !SUBMITTING_STATUSES.has(order.status) && !TERMINAL_STATUSES.has(order.status) && order.status !== 'awaiting_approval')

  // Determine header message based on status
  const getHeaderMessage = () => {
    if (anyRequiresApproval) {
      const routedToApproverText = orders.length === 1
        ? 'Your order has been sent to your approver.'
        : `Your ${orders.length} orders have been sent to your approver.`
      return {
        title: 'Submitted for approval',
        subtitle: anyWeeklyLimit
          ? (
              <>
                <span className="text-amber-600 font-medium">
                  You've exceeded your weekly spending threshold.
                </span>{' '}
                {routedToApproverText}
              </>
            )
          : (orders.length === 1
              ? "This order requires approval. You'll be notified once your approver reviews it."
              : "These orders require approval. You'll be notified once your approver reviews them."),
        icon: Check,
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
      }
    }

    if (anySubmitting) {
      return {
        title: orders.length === 1 ? 'Order processing' : 'Orders processing',
        subtitle: 'You can track the status in your order details',
        icon: Clock,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
      }
    }

    if (allPlaced) {
      return {
        title: orders.length === 1 ? 'Order placed successfully' : 'Orders placed successfully',
        subtitle:
          orders.length === 1
            ? 'Your order has been sent to the supplier'
            : `Your ${orders.length} orders have been sent to the suppliers`,
        icon: CheckCircle,
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
      }
    }

    // Default/mixed status
    return {
      title: 'Orders submitted',
      subtitle: 'Check individual order statuses below',
      icon: CheckCircle,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    }
  }

  const headerMessage = getHeaderMessage()
  const HeaderIcon = headerMessage.icon

  // Items left in the cart after this checkout (mock: remaining-cart banner).
  const remainingCart = cart ?? []
  const remainingItemCount = remainingCart.reduce((sum, item) => sum + item.quantity, 0)
  const remainingSupplierCount = new Set(remainingCart.map((item) => item.supplierId)).size

  // Get status badge for individual order — shows actual internal status
  const getOrderStatusBadge = (order: OrderData) => {
    const label = getStatusLabel(order.status)

    // Needs Attention (Amber)
    if (order.status === 'awaiting_approval') {
      return (
        <div className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <ShieldAlert className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Submitting (Sky) — retry loop before supplier acknowledges. Own colour
    // distinct from "Sent to supplier" (blue) below.
    if (order.status === 'started_processing') {
      return (
        <div className="flex items-center gap-1.5 bg-sky-50 text-sky-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <Clock className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Approved / Sent to supplier (Blue) — tenant approved, or supplier has
    // the order and we're waiting on their ack.
    if (order.status === 'approved' || order.status === 'awaiting_supplier') {
      return (
        <div className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <Clock className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Processing (Indigo)
    if (order.status === 'processing') {
      return (
        <div className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <Package className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // In Transit (Purple)
    if (order.status === 'out_for_delivery') {
      return (
        <div className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <Truck className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Success (Green)
    if (order.status === 'ready_to_collect' || order.status === 'completed') {
      return (
        <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <CheckCircle className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Failed/Declined (Red)
    if (order.status === 'submission_failed' || order.status === 'internal_decline' ||
        order.status === 'fulfillment_declined' || order.status === 'supplier_cancelled') {
      return (
        <div className="flex items-center gap-1.5 bg-red-50 text-red-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <AlertCircle className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Cancelled (Gray)
    if (order.status === 'cancelled') {
      return (
        <div className="flex items-center gap-1.5 bg-gray-50 text-gray-700 px-2.5 py-1 rounded-lg text-xs font-medium">
          <Ban className="w-3.5 h-3.5" />
          {label}
        </div>
      )
    }

    // Default
    return (
      <div className="flex items-center gap-1.5 bg-gray-50 text-gray-700 px-2.5 py-1 rounded-lg text-xs font-medium">
        <AlertCircle className="w-3.5 h-3.5" />
        {label}
      </div>
    )
  }

  return (
    <EngineerLayout>
      <main className="max-w-3xl mx-auto px-4 py-12 pb-32">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className={`w-20 h-20 rounded-full ${headerMessage.iconBg} flex items-center justify-center`}>
            <HeaderIcon className={`w-12 h-12 ${headerMessage.iconColor}`} />
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center mb-8 px-2">
          <h1 className="text-gray-900 text-2xl font-bold mb-2">{headerMessage.title}</h1>
          <p className="text-gray-500 text-sm">{headerMessage.subtitle}</p>
        </div>

        {/* Order Cards */}
        <div className="space-y-4 mb-8">
          {orders.map((order) => {
            // DGM-003: the backend decides whether and what to show (per-supplier
            // SLA, status gating, cutoff fit, requested-date guard). The surface
            // just renders whatever the server returns. Exactly one of
            // collection/delivery is non-null per order (gated by fulfillment_type).
            const collectionGuidance = order.collectionGuidance
            const deliveryGuidance = order.deliveryGuidance
            return (
            <Fragment key={order.id}>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Card header strip (mock: "Order Summary") */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="text-gray-900 font-bold text-sm">Order summary</h3>
              </div>

              {/* Card body */}
              <div className="p-4 sm:p-5">
              {/* Order Details */}
              <div className="space-y-3 mb-4">
                {/* Booked date (DEV-51011 AC9) */}
                <div>
                  <p className="flex items-center gap-2 text-sm font-bold text-gray-900">
                    {order.fulfillmentType === 'delivery' ? (
                      <Truck className="w-4 h-4 text-gray-500" aria-hidden="true" />
                    ) : (
                      <Package className="w-4 h-4 text-gray-500" aria-hidden="true" />
                    )}
                    {order.bookedDate
                      ? `${order.fulfillmentType === 'delivery' ? 'Delivery on' : 'Collect on'} ${formatShortDate(order.bookedDate)}`
                      : `${order.fulfillmentType === 'delivery' ? 'Delivery' : 'Collection'} date to be confirmed by the branch`}
                  </p>
                  {order.bookedDate && order.isBookedDateUnconfirmed && (
                    <p className="mt-0.5 pl-6 text-xs text-gray-500">To be confirmed by the branch</p>
                  )}
                </div>
                {/* Order ID (with copy) */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Order ID</span>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-gray-900 font-bold truncate">#{order.orderNumber}</span>
                    <button
                      type="button"
                      onClick={() => handleCopyOrder(order)}
                      className="p-1 rounded-md hover:bg-gray-100 active:scale-95 transition-all flex-shrink-0"
                      title="Copy order details"
                      aria-label="Copy order details"
                    >
                      {copiedId === order.id ? (
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                {/* Job number (mock's Transaction ID slot; hyphen when absent) */}
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-500">Job number</span>
                  {order.jobNumber ? (
                    <span className="text-gray-900 font-medium text-right truncate">{order.jobNumber}</span>
                  ) : (
                    // Line the hyphen up under the '#': the order number is a
                    // fixed-format value, so mirror it invisibly to reserve its
                    // exact width (mr-7 skips the copy-icon zone), then overlay the
                    // hyphen at that block's left edge — the '#' position.
                    <span className="relative mr-7 font-bold invisible">#{order.orderNumber}<span className="visible absolute left-0 font-normal text-gray-400">-</span></span>
                  )}
                </div>
                {/* Status */}
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-500">Status</span>
                  <div className="flex-shrink-0">{getOrderStatusBadge(order)}</div>
                </div>
              </div>

              {/* Warning message if submission had issues */}
              {order.submissionWarning && (
                <div className="mt-3 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <p className="text-orange-700 text-xs">{order.submissionWarning}</p>
                </div>
              )}

              {/* Collection guidance (DGM-003) — all suppliers; backend decides visibility. */}
              {collectionGuidance && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-800 text-xs leading-snug">{collectionGuidance.text}</p>
                  </div>
                </div>
              )}
              {/* Delivery guidance (DGM-004) — all suppliers; backend decides visibility. */}
              {deliveryGuidance && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <Clock className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-800 text-xs leading-snug">{deliveryGuidance.text}</p>
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Approval Routing (mock) — only for approval-state orders */}
            {order.requiresApproval && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h3 className="text-gray-900 font-bold text-sm">Approval routing</h3>
                </div>
                <div className="p-4 sm:p-5 space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">Sent to</span>
                    <span className="text-gray-900 font-medium text-right truncate">
                      {order.assignedApprover || '—'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs">
                    {order.assignedApprover
                      ? "Your approver will review this order. You'll be notified by email once it's approved or declined."
                      : 'You will be notified once it has been approved or declined.'}
                  </p>
                </div>
              </div>
            )}
            </Fragment>
            )
          })}
        </div>

        {/* Items remaining in cart (mock) — shown only when the cart still has
            items after this checkout. */}
        {remainingCart.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-amber-700 font-bold text-sm">Items remaining in basket</p>
                <p className="text-gray-600 text-xs mt-0.5">
                  {remainingItemCount} {remainingItemCount === 1 ? 'item' : 'items'} from{' '}
                  {remainingSupplierCount} {remainingSupplierCount === 1 ? 'supplier' : 'suppliers'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/${tenantSlug}/engineer/cart`)}
                className="flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-lg px-3 py-2 transition-colors"
              >
                View basket
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons (mock: 2-col white bordered) */}
        <div className="grid grid-cols-2 gap-3">
          {/* View Order Details - only for single order */}
          {orders.length === 1 && (
            <button
              type="button"
              onClick={() => navigate(`/${tenantSlug}/engineer/order/${orders[0].id}`)}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3.5 text-gray-900 font-bold text-sm hover:bg-gray-50 transition-colors text-center"
            >
              View order
            </button>
          )}

          {/* Continue Shopping */}
          <button
            type="button"
            onClick={() => navigate(`/${tenantSlug}/engineer/search`)}
            className={`bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3.5 text-gray-900 font-bold text-sm hover:bg-gray-50 transition-colors text-center ${orders.length === 1 ? '' : 'col-span-2'}`}
          >
            Continue shopping
          </button>
        </div>
      </main>
    </EngineerLayout>
  )
}
